import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {Person, Star} from "@mui/icons-material";
import {useUserAuth} from "../../context/UserAuthContext";
import {
    ACADEMIC_TITLES,
    DEFAULT_ENGINEER_PHOTO,
    GENDERS,
    getMyAppliedProjects,
    getMyApprovedProjects,
    getEngineerPoints,
    updateEngineer,
    validateEngineerProfileEdit,
} from "../../services/engineerService";
import {COLLEGES} from "../../services/associationService";
import {ProjectAvatar} from "../common/ProjectImage";
import PortalTopbar from "../Association/PortalTopbar";

const PRIMARY = "#3E4F88";

/**
 * Perfil do engenheiro (Fase 2) — espelha o ProfilePage + EditProfilePage da app:
 *   - dados do perfil (foto, nome, curso, nº OE/estudante, email, pontos)
 *   - "Projetos ativos (N)"  -> projetos aprovados
 *   - "Candidaturas (N)"     -> candidaturas pendentes
 *   - "Editar perfil"        -> mesmos campos editáveis da app
 * (O "apagar conta" da app não foi trazido para o web — o dashboard de admin
 *  já permite remover engenheiros.)
 */
const EngineerProfile = () => {

    const {profile, refreshProfile} = useUserAuth();
    const navigate = useNavigate();

    const [points, setPoints] = useState(null);
    const [applied, setApplied] = useState([]);
    const [approved, setApproved] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editing, setEditing] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [gender, setGender] = useState("");
    const [profileType, setProfileType] = useState("Membro OE");
    const [oeNumber, setOeNumber] = useState("");
    const [university, setUniversity] = useState("");
    const [studentNumber, setStudentNumber] = useState("");
    const [academicTitle, setAcademicTitle] = useState("");
    const [college, setCollege] = useState("");
    const [company, setCompany] = useState("");
    const [address, setAddress] = useState("");
    const [subcollege, setSubcollege] = useState("");
    const [phone, setPhone] = useState("");
    const [linkedin, setLinkedin] = useState("");
    const [info, setInfo] = useState("");

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!profile || !profile.Id) return;
        try {
            const [p, ap, app] = await Promise.all([
                getEngineerPoints(profile.Id),
                getMyApprovedProjects(profile),
                getMyAppliedProjects(profile),
            ]);
            setPoints(p);
            setApproved(ap);
            setApplied(app);
        } catch (err) {
            console.error("Erro ao carregar perfil:", err);
        } finally {
            setLoading(false);
        }
    }, [profile]);

    useEffect(() => {
        load();
    }, [load]);

    if (!profile) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Alert severity="warning">
                        Não foi possível carregar o teu perfil.
                    </Alert>
                </Container>
            </Box>
        );
    }

    const startEdit = () => {
        setFirstName(profile.FirstName || "");
        setLastName(profile.LastName || "");
        setGender(profile.Gender || "");
        setProfileType(profile.ProfileType === "Estudante" ? "Estudante" : "Membro OE");
        setOeNumber(profile.OENumber || "");
        setUniversity(profile.University || "");
        setStudentNumber(profile.StudentNumber || "");
        setAcademicTitle(profile.AcademicTitles || "");
        setCollege(profile.Course || "");
        setCompany(profile.Company || "");
        setAddress(profile.Address || "");
        setSubcollege(profile.Subcollege || "");
        setPhone(profile.PhoneNumber || "");
        setLinkedin(profile.Linkedin || "");
        setInfo(profile.OtherInfo || "");
        setError("");
        setSuccess("");
        setEditing(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const problem = validateEngineerProfileEdit({
            firstName,
            lastName,
            gender,
            profileType,
            oeNumber,
            university,
            studentNumber,
            academicTitle,
            college,
        });
        if (problem) {
            setError(problem);
            return;
        }

        setSaving(true);
        try {
            // Atualiza o registo completo, preservando os campos de gestão
            // (Id, Email, ProfileURL, Enabled, Points, RegisterDate, etc.) —
            // igual à abordagem do EditProfilePage da app.
            const {key, ...record} = profile;
            const updated = {
                ...record,
                FirstName: firstName.trim(),
                LastName: lastName.trim(),
                Gender: gender,
                AcademicTitles: academicTitle,
                Course: college,
                Company: company.trim(),
                Address: address.trim(),
                Subcollege: subcollege.trim(),
                PhoneNumber: phone.trim(),
                OENumber: profileType === "Membro OE" ? oeNumber.trim() : "",
                OtherInfo: info.trim(),
                Linkedin: linkedin.trim(),
                ProfileType: profileType,
                StudentNumber: profileType === "Estudante" ? studentNumber.trim() : "",
                University: profileType === "Estudante" ? university.trim() : "",
            };
            await updateEngineer(profile.key, updated);
            await refreshProfile();
            setEditing(false);
            setSuccess("Dados atualizados com sucesso.");
        } catch (err) {
            console.error("Erro ao atualizar perfil:", err);
            setError("Erro ao atualizar os dados. Tenta novamente.");
        } finally {
            setSaving(false);
        }
    };

    const detail = (label, value) => (
        <Box sx={{minWidth: 160}}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 0.25}}>
                {label}
            </Typography>
            <Typography variant="body1">{value || "—"}</Typography>
        </Box>
    );

    const projectCard = (p) => (
        <Card key={p.key}>
            <CardActionArea onClick={() => navigate(`/app/engenheiro/projetos/${p.Id}`)} sx={{p: 1.5, display: "flex", alignItems: "center", gap: 2}}>
                <ProjectAvatar src={p.Logo} alt={p.Title} size={48}/>
                <CardContent sx={{p: 0, flex: 1}}>
                    <Typography variant="body1" sx={{fontWeight: 600, color: PRIMARY}}>
                        {p.Title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {p.Association} · {p.College}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );

    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="md" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 4}}>
                        {success && <Alert severity="success" sx={{mb: 2}}>{success}</Alert>}
                        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

                        <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems="center">
                            <Avatar
                                src={profile.ProfileURL || DEFAULT_ENGINEER_PHOTO}
                                sx={{width: 110, height: 110, border: 2, borderColor: PRIMARY}}
                            >
                                <Person fontSize="large"/>
                            </Avatar>
                            <Box sx={{flex: 1}}>
                                <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                                    {profile.FirstName} {profile.LastName}
                                </Typography>
                                <Typography color="text.secondary">
                                    {profile.Email}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{mt: 1}} flexWrap="wrap" useFlexGap>
                                    {profile.ProfileType && <Chip label={profile.ProfileType} size="small" color="primary" variant="outlined"/>}
                                    {profile.Course && <Chip label={`🎓 ${profile.Course}`} size="small" variant="outlined"/>}
                                    {points !== null && (
                                        <Chip
                                            icon={<Star/>}
                                            label={`${points} ponto(s)`}
                                            size="small"
                                            sx={{bgcolor: "#fff8e1", color: "#8d6e00"}}
                                        />
                                    )}
                                </Stack>
                            </Box>
                            {!editing && (
                                <Button
                                    variant="outlined"
                                    sx={{color: PRIMARY, borderColor: PRIMARY, fontWeight: 600}}
                                    onClick={startEdit}
                                >
                                    Editar perfil
                                </Button>
                            )}
                        </Stack>

                        <Divider sx={{my: 3}}/>

                        {!editing ? (
                            <>
                                <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)"}}}>
                                    {profile.ProfileType === "Estudante" ? (
                                        <>
                                            {detail("Instituição", profile.University)}
                                            {detail("Nº de estudante", profile.StudentNumber)}
                                        </>
                                    ) : (
                                        detail("Nº OE", profile.OENumber)
                                    )}
                                    {detail("Título académico", profile.AcademicTitles)}
                                    {detail("Empresa", profile.Company)}
                                    {detail("Telefone", profile.PhoneNumber)}
                                    {detail("Morada", profile.Address)}
                                    {detail("Área de trabalho", profile.Subcollege)}
                                    {detail("LinkedIn", profile.Linkedin)}
                                </Box>
                                {profile.OtherInfo && (
                                    <>
                                        <Divider sx={{my: 2}}/>
                                        {detail("Outras informações", profile.OtherInfo)}
                                    </>
                                )}
                                {profile.RegisterDate && (
                                    <Typography variant="caption" color="text.disabled" display="block" sx={{mt: 2}}>
                                        Membro desde {profile.RegisterDate}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <form onSubmit={save} noValidate>
                                <Stack spacing={2.5}>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Primeiro nome"
                                            size="small"
                                            required
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                        <TextField
                                            label="Último nome"
                                            size="small"
                                            required
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Género"
                                            size="small"
                                            select
                                            required
                                            value={gender}
                                            onChange={(e) => setGender(e.target.value)}
                                            sx={{flex: 1}}
                                        >
                                            {GENDERS.map((g) => (
                                                <MenuItem key={g} value={g}>{g}</MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            label="Título académico"
                                            size="small"
                                            select
                                            required
                                            value={academicTitle}
                                            onChange={(e) => setAcademicTitle(e.target.value)}
                                            sx={{flex: 1}}
                                        >
                                            {ACADEMIC_TITLES.map((t) => (
                                                <MenuItem key={t} value={t}>{t}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Stack>
                                    <TextField
                                        label="Colégio da engenharia"
                                        size="small"
                                        select
                                        required
                                        value={college}
                                        onChange={(e) => setCollege(e.target.value)}
                                    >
                                        {COLLEGES.map((c) => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </TextField>

                                    <Box>
                                        <Typography variant="body2" fontWeight={600} sx={{mb: 1}}>
                                            Tipo de perfil *
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={profileType}
                                            onChange={(e) => setProfileType(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="Membro OE"
                                                control={<Radio sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}/>}
                                                label="Membro da Ordem dos Engenheiros"
                                            />
                                            <FormControlLabel
                                                value="Estudante"
                                                control={<Radio sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}/>}
                                                label="Estudante"
                                            />
                                        </RadioGroup>

                                        {profileType === "Membro OE" ? (
                                            <TextField
                                                label="Número OE"
                                                size="small"
                                                required
                                                value={oeNumber}
                                                onChange={(e) => setOeNumber(e.target.value)}
                                                sx={{mt: 1}}
                                            />
                                        ) : (
                                            <Stack direction={{xs: "column", sm: "row"}} spacing={2} sx={{mt: 1}}>
                                                <TextField
                                                    label="Instituição de ensino"
                                                    size="small"
                                                    required
                                                    value={university}
                                                    onChange={(e) => setUniversity(e.target.value)}
                                                    sx={{flex: 2}}
                                                />
                                                <TextField
                                                    label="Número de estudante"
                                                    size="small"
                                                    required
                                                    value={studentNumber}
                                                    onChange={(e) => setStudentNumber(e.target.value)}
                                                    sx={{flex: 1}}
                                                />
                                            </Stack>
                                        )}
                                    </Box>

                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Empresa"
                                            size="small"
                                            value={company}
                                            onChange={(e) => setCompany(e.target.value)}
                                            sx={{flex: 2}}
                                        />
                                        <TextField
                                            label="Telefone"
                                            size="small"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Morada"
                                            size="small"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            sx={{flex: 2}}
                                        />
                                        <TextField
                                            label="Área de trabalho"
                                            size="small"
                                            value={subcollege}
                                            onChange={(e) => setSubcollege(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                    <TextField
                                        label="LinkedIn"
                                        size="small"
                                        value={linkedin}
                                        onChange={(e) => setLinkedin(e.target.value)}
                                    />
                                    <TextField
                                        label="Outras informações"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        value={info}
                                        onChange={(e) => setInfo(e.target.value)}
                                    />

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={saving}
                                            sx={{bgcolor: PRIMARY, fontWeight: 600}}
                                        >
                                            {saving ? "A guardar…" : "Guardar alterações"}
                                        </Button>
                                        <Button
                                            onClick={() => setEditing(false)}
                                            color="inherit"
                                            sx={{textTransform: "none"}}
                                        >
                                            Cancelar
                                        </Button>
                                    </Stack>
                                </Stack>
                            </form>
                        )}
                    </Box>

                    {/* Projetos ativos (aprovados) — igual à app */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 2}}>
                            Projetos ativos ({approved.length})
                        </Typography>
                        {loading ? (
                            <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                                <CircularProgress size={28}/>
                            </Box>
                        ) : approved.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Ainda não foste aprovado para nenhum projeto.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>{approved.map(projectCard)}</Stack>
                        )}
                    </Box>

                    {/* Candidaturas pendentes — igual à app */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 2}}>
                            Candidaturas ({applied.length})
                        </Typography>
                        {loading ? (
                            <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                                <CircularProgress size={28}/>
                            </Box>
                        ) : applied.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Ainda não te candidataste a nenhum projeto.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>{applied.map(projectCard)}</Stack>
                        )}
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default EngineerProfile;
